import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import z from "zod";
import { eq, inArray } from "drizzle-orm";
import { auth } from "../better-auth/auth";
import { db } from "~/db";
import {
  locationInquiry,
  location,
  locationsToUsers,
  user,
} from "~/db/schema";
import { getTemplateByKey } from "~/lib/email/getEmailTemplatePartials";
import { sendEmail } from "~/lib/email/sendEmail";

const appointmentSchema = z.object({
  start: z.coerce.date(),
  end: z.coerce.date(),
});

const createLocationInquirySchema = z.object({
  userId: z.string().optional(),
  locationIds: z.array(z.number()),
  // Contact form fields
  salutation: z.enum(["Frau", "Herr", "Divers"]),
  firstName: z.string().min(1).max(100),
  lastName: z.string().min(1).max(100),
  company: z.string().max(200).optional(),
  email: z.string().email(),
  telephone: z.string().min(1),
  // Schedule - appointments
  appointments: z.array(appointmentSchema),
  // Details form fields
  eventTypeId: z.number(),
  businessType: z.enum(["Privat", "Geschäftlich"]),
  numberOfRooms: z.number().int().positive().optional(),
  numberOfGuests: z.number().int().positive().optional(),
  budgetType: z.enum(["Gesamt", "Pro Person"]),
  budget: z.number().positive().optional(),
  budgetCurrency: z.string().default("EUR"),
  notes: z.string().optional(),
  requestedRooms: z
    .array(
      z.object({
        id: z.number(),
        name: z.string(),
      }),
    )
    .optional(),
});

export const createLocationInquiry = createServerFn({ method: "POST" })
  .inputValidator(createLocationInquirySchema)
  .handler(async (ctx) => {
    const request = getRequest();

    if (!request?.headers) {
      throw new Error("Request headers not available");
    }

    const userSession = await auth.api.getSession({ headers: request.headers });

    const {
      userId,
      locationIds,
      salutation,
      firstName,
      lastName,
      company,
      email,
      telephone,
      appointments,
      eventTypeId,
      businessType,
      numberOfRooms,
      numberOfGuests,
      budgetType,
      budget,
      budgetCurrency,
      notes,
      requestedRooms,
    } = ctx.data;

    // Create one inquiry per location
    // For anonymous users, userId will be null - location owner will contact via email
    const inquiries = locationIds.map((locationId) => ({
      locationId,
      userId: userSession?.user.id || userId || null,
      status: "open" as const,
      salutation,
      firstName,
      lastName,
      company: company || null,
      email,
      telephone,
      eventTypeId,
      businessType,
      numberOfRooms: numberOfRooms || null,
      numberOfGuests: numberOfGuests || null,
      budgetType,
      budget: budget || null,
      budgetCurrency: budgetCurrency || "EUR",
      notes: notes || null,
      appointments: JSON.stringify(appointments),
      requestedRooms: requestedRooms || null,
    }));

    await db.insert(locationInquiry).values(inquiries);

    // Send emails to location owners and to the user for each location
    const baseUrl = process.env.VITE_BASE_URL ?? "";
    const locationIdsUnique = [...new Set(locationIds)];

    const locationNames = await db
      .select({ id: location.id, name: location.name })
      .from(location)
      .where(inArray(location.id, locationIdsUnique));
    const nameById = new Map(locationNames.map((r) => [r.id, r.name]));

    const locationsWithOwners = await db
      .select({
        locationId: location.id,
        locationName: location.name,
        ccEmails: location.ccEmails,
        ownerEmail: user.email,
      })
      .from(location)
      .innerJoin(locationsToUsers, eq(location.id, locationsToUsers.locationId))
      .innerJoin(user, eq(locationsToUsers.userId, user.id))
      .where(inArray(location.id, locationIdsUnique));

    // Group by location (one location can have multiple owners)
    const locationInfo = new Map<
      number,
      { name: string; ownerEmails: string[]; ccEmails: string[] }
    >();
    for (const row of locationsWithOwners) {
      const cur = locationInfo.get(row.locationId);
      if (!cur) {
        locationInfo.set(row.locationId, {
          name: row.locationName,
          ownerEmails: row.ownerEmail ? [row.ownerEmail] : [],
          ccEmails:
            Array.isArray(row.ccEmails) && row.ccEmails.every((v) => typeof v === "string")
              ? row.ccEmails
              : typeof row.ccEmails === "string"
                ? (() => {
                    try {
                      const parsed = JSON.parse(row.ccEmails);
                      return Array.isArray(parsed)
                        ? parsed.filter((v) => typeof v === "string")
                        : [];
                    } catch {
                      return [];
                    }
                  })()
                : [],
        });
      } else if (row.ownerEmail && !cur.ownerEmails.includes(row.ownerEmail)) {
        cur.ownerEmails.push(row.ownerEmail);
      }
    }

    const ownerTemplate = await getTemplateByKey("newInquiryOwner");
    const userTemplate = await getTemplateByKey("newInquiryUser");

    // Replace plain placeholders and TYPO3 link placeholder (t3://page?uid=...)
    const replaceVars = (text: string, locationLink: string, locationName: string) =>
      text
        .replace(/t3:\/\/page\?uid=%7BLOCATION_LINK%7D/gi, locationLink)
        .replace(/t3:\/\/page\?uid=\{LOCATION_LINK\}/gi, locationLink)
        .replace(/\{LOCATION_LINK\}/g, locationLink)
        .replace(/\{LOCATION_NAME\}/g, locationName);

    for (const locId of locationIdsUnique) {
      const info = locationInfo.get(locId);
      const locationName = info?.name ?? nameById.get(locId) ?? "";
      const locationLink = `${baseUrl}/location/${locId}`;

      if (ownerTemplate && info?.ownerEmails.length) {
        const subject = replaceVars(ownerTemplate.subject, locationLink, locationName);
        const html = replaceVars(ownerTemplate.body, locationLink, locationName);
        const text = html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
        for (const to of info.ownerEmails) {
          await sendEmail({ to, cc: info.ccEmails, subject, text, html });
        }
      }

      if (userTemplate) {
        const subject = replaceVars(userTemplate.subject, locationLink, locationName);
        const html = replaceVars(userTemplate.body, locationLink, locationName);
        const text = html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
        await sendEmail({ to: email, subject, text, html });
      }
    }

    return { success: true, count: inquiries.length };
  });
